import { Drawer, IconButton, Tooltip } from '@evaneos/b2b-ui';
import CloseIcon from '@evaneos/b2b-ui/icons/Close';
import UnfoldMoreIcon from '@evaneos/b2b-ui/icons/UnfoldMore';
import UnfoldLessIcon from '@evaneos/b2b-ui/icons/UnfoldLess';
import { useIntl } from 'react-intl';

import { Contract } from '../Contract/Contract';
import { RecordedCallDetails } from '../Discussion/components/RecordedCallDetails/RecordedCallDetails';
import { MemoExpandedEditor } from '../DossierMainInfo/EditableItems/MemoExpandedEditor';

import { useSidePanel } from './SidePanelContext';

export const DRAWER_WIDTH = 420;

export const SidePanel = () => {
    const { formatMessage } = useIntl();
    const { isOpen, closePanel, isResizable, isExpanded, toggleExpanded, panelState, drawerWidth } =
        useSidePanel();

    const renderPanelContent = () => {
        const viewMode = isExpanded ? 'expanded' : 'compact';

        switch (panelState.type) {
            case 'contract':
                return <Contract viewMode={viewMode} />;
            case 'phone-call-details':
                return <RecordedCallDetails phoneCall={panelState.data} />;
            case 'note-editor':
                return <MemoExpandedEditor dossierId={panelState.data.dossierId} />;
            default:
                return null;
        }
    };

    const content = renderPanelContent();

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={closePanel}
            variant="persistent"
            slotProps={{
                paper: {
                    elevation: isExpanded ? 8 : 0,
                    sx: (theme) => ({
                        width: `${drawerWidth}px`,
                        maxWidth: '100%',
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        zIndex: 1000,
                        backgroundColor: 'var(--color-beige)',
                        borderLeft: { md: '1px solid' },
                        borderColor: { md: 'divider' },
                        willChange: 'width, transform',
                        transition: `${[
                            theme.transitions.create('width', {
                                duration: theme.transitions.duration.standard,
                                easing: theme.transitions.easing.easeOut,
                            }),
                            theme.transitions.create('transform', {
                                duration: theme.transitions.duration.standard,
                                easing: isOpen
                                    ? theme.transitions.easing.easeOut
                                    : theme.transitions.easing.sharp,
                            }),
                        ].join(', ')} !important`,
                    }),
                },
            }}
            sx={{ display: 'block' }}
        >
            <IconButton
                onClick={closePanel}
                sx={{
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    zIndex: 1,
                }}
                aria-label="close"
            >
                <CloseIcon />
            </IconButton>
            {isResizable && (
                <Tooltip
                    disableInteractive
                    title={formatMessage({
                        id: isExpanded
                            ? 'dossier.side_panel.collapse'
                            : 'dossier.side_panel.expand',
                    })}
                >
                    <IconButton
                        onClick={toggleExpanded}
                        sx={{
                            display: { xs: 'none', sm: 'inline-flex' },
                            position: 'absolute',
                            right: 60,
                            top: 16,
                            zIndex: 1,
                            '& svg': {
                                transform: 'rotate(45deg)',
                            },
                        }}
                        aria-label={formatMessage({
                            id: isExpanded
                                ? 'dossier.side_panel.collapse'
                                : 'dossier.side_panel.expand',
                        })}
                    >
                        {isExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                    </IconButton>
                </Tooltip>
            )}
            {content}
        </Drawer>
    );
};
